<template>
  <div class="animated fadeIn">

    <b-card class="card-accent-info" header="Monitor por Setor">
      <b-card-body>
        <b-row>
          <b-col cols="auto">
            <b-form-group>
                <label for="company" style="font-size: 0.8em">Empresa</label>
                <b-form-select id="company"
                  v-model="filters.companyId"
                  :plain="true"
                  :options="companyDDL"
                  @change="changeCompany">
                </b-form-select>
            </b-form-group>
          </b-col>

          <b-col cols="auto">
            <b-form-group>
                <label for="inventory" style="font-size: 0.8em">Inventário {{ inventoryLabelEnd }}</label>
                <b-form-select id="inventory"
                  v-model="filters.inventoryId"
                  :plain="true"
                  :options="inventoryDDL"
                  @change="changeInventory">
                </b-form-select>
            </b-form-group>
          </b-col>

          <b-col cols="auto">
            <b-form-group>
                <label for="statusFilter" style="font-size: 0.8em">Exibir</label>
                <b-form-select id="statusFilter"
                  v-model="filters.status"
                  :plain="true"
                  :options="statusOptions">
                </b-form-select>
            </b-form-group>
          </b-col>

          <b-col cols="auto" class="mr-auto" style="padding-top: 12px;">
            <b-form-group>
              <p></p>
              <b-button variant="stack-overflow" class="mr-1 btn-brand" @click="refreshData()"><i class="fa fa-search"></i><span>Atualizar</span></b-button>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="mb-3">
          <b-col>
            <div class="d-flex align-items-center">
              <span class="text-muted"><strong>Setores:</strong> {{ filteredRows.length }}<span v-if="filteredRows.length !== rows.length"> de {{ rows.length }}</span></span>
              <b-spinner small variant="primary" class="ml-2" v-if="isBusy"></b-spinner>
              <span v-if="isBusy" class="text-primary ml-1" style="font-size: 0.9em">Carregando {{ loaded }} / {{ sectorTotal }}...</span>
            </div>
          </b-col>
        </b-row>

        <b-table
            :items="filteredRows"
            :fields="fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :primary-key="primaryKey"
            sort-icon-left
            responsive
            small
            hover
            striped
            class="monitor-sector-table"
            show-empty
            empty-text="Sem dados">

            <template v-for="col in columns" v-slot:[`cell(${col.key}_pct)`]="data">
              {{ $utils.numberFormat(data.item[col.key + '_pct'], 1) }}
            </template>

            <template #custom-foot="scope">
              <tr v-for="line in footLines" :key="line.label">
                <th v-for="f in scope.fields" :key="f.key" :class="f.key == 'sectorName' ? '' : 'text-center'">
                  <template v-if="f.key == 'sectorName'">{{ line.label }}</template>
                  <template v-else-if="line.data && line.data[f.key] != null">
                    <template v-if="f.key.endsWith('_pct')">{{ $utils.numberFormat(line.data[f.key], 1) }}</template>
                    <template v-else>{{ line.data[f.key] }}</template>
                  </template>
                </th>
              </tr>
            </template>

        </b-table>
      </b-card-body>
    </b-card>

  </div>
</template>

<script>
import axios from "axios";

export default {
  name: 'InventoryMonitorSector',
  data: () => {
    return {
      companyDDL: [],
      inventoryDDL: [],
      sectorDDL: [],
      // Colunas dinâmicas, uma por item do monitor: { key, label }
      columns: [],
      rows: [],
      totals: null,
      isBusy: false,
      loaded: 0,
      sectorTotal: 0,
      // Descarta respostas de chamadas antigas quando inventário/empresa mudam durante o carregamento
      refreshToken: 0,
      intervalId: null,
      sortBy: 'sectorName',
      sortDesc: false,
      primaryKey: 'sectorId',
      statusOptions: [
        { value: 'all', text: 'Todos' },
        { value: 'done', text: 'Finalizados' },
        { value: 'pending', text: 'Não finalizados' }
      ],
      filters: {
        companyId: null,
        inventoryId: 0,
        status: 'all'
      }
    }
  },
  computed: {
    // Finalizado = linha verde (_rowVariant === 'success')
    filteredRows() {
      var status = this.filters.status
      if (status === 'done') return this.rows.filter(r => r._rowVariant === 'success')
      if (status === 'pending') return this.rows.filter(r => r._rowVariant !== 'success')
      return this.rows
    },
    // Subtotal dos setores exibidos: soma das quantidades; o percentual é recalculado com o mesmo
    // divisor que o backend usa em cada coluna (etiquetas do setor, identificadas ou total).
    subtotal() {
      var rows = this.filteredRows
      if (rows.length === 0) return null

      var divisors = {
        labels: r => r._labels || 0,
        identified: r => r.c_IDENTIFIED_TOTAL || 0,
        total: r => r.c_TOTAL || 0
      }
      var sub = {}

      this.columns.forEach(c => {
        var qty = rows.reduce((a, r) => a + (r[c.key] || 0), 0)
        sub[c.key] = qty

        // Descobre qual divisor gera o percentual desta coluna, olhando uma linha com valor > 0
        var kind = null
        for (var i = 0; i < this.rows.length && !kind; i++) {
          var r = this.rows[i]
          var p = r[c.key + '_pct']
          if (p > 0 && r[c.key] > 0) {
            var implied = r[c.key] * 100 / p
            kind = Object.keys(divisors).find(k => Math.abs(divisors[k](r) - implied) < 0.5) || null
          }
        }

        var den = kind ? rows.reduce((a, r) => a + divisors[kind](r), 0) : 0
        sub[c.key + '_pct'] = den > 0 ? qty * 100 / den : 0
      })
      return sub
    },
    footLines() {
      var lines = []
      if (this.subtotal) lines.push({ label: 'Subtotal', data: this.subtotal })
      if (this.totals) lines.push({ label: 'Total', data: this.totals })
      return lines
    },
    inventoryLabelEnd() {
      var inv = this.inventoryDDL.find(item => item.value === this.filters.inventoryId)
      return inv && inv.labelEnd != null ? inv.labelEnd : "-"
    },
    fields() {
      var cols = []
      // Divergente com percentual > 0: quantidade e % em vermelho e negrito
      // 1ª/2ª/3ª contagem parcial (> 0 e < 100): idem
      var alert = 'text-danger font-weight-bold'
      var divergent = (value, key, item) => item.c_COUNT2_pct > 0 ? alert : ''
      var partial = (pctKey) => (value, key, item) => item[pctKey] > 0 && item[pctKey] < 100 ? alert : ''
      this.columns.forEach(c => {
        var cls = ''
        if (c.key == 'c_COUNT2') cls = divergent
        else if (['c_COUNT1_TOTAL', 'c_COUNT2_TOTAL', 'c_COUNT3'].includes(c.key)) cls = partial(c.key + '_pct')
        cols.push({ key: c.key, label: c.label, sortable: true, class: 'text-center', tdClass: cls })
        cols.push({ key: c.key + '_pct', label: '%', sortable: true, class: 'text-center', tdClass: cls })
      })
      return [{ key: 'sectorName', label: 'Setor', sortable: true }].concat(cols)
    }
  },
  methods: {
    // Converte a resposta do monitor em colunas e valores (quantidade + percentual) de uma linha
    toRow(sector, monitor) {
      var row = { sectorId: sector.value, sectorName: sector.text }
      var cols = []
      ;(monitor || []).forEach(m => {
        var key = 'c_' + m.code
        // Total de etiquetas do setor, vindo no título do card TOTAL, ex.: "TOTAL (10)"
        if (m.code == 'TOTAL') {
          var match = /\((\d+)\)\s*$/.exec(m.title || '')
          row._labels = match ? Number(match[1]) : 0
        }
        row[key] = m.quantity != null ? m.quantity : 0
        row[key + '_pct'] = m.percentage != null ? Number(m.percentage) : 0
        // O título pode vir com o total do setor entre parênteses, ex.: "TOTAL (10)"; não vale para a tabela toda
        cols.push({ key: key, label: (m.title || '').replace(/\s*\(\d+\)\s*$/, '') })
      })
      if (this.isComplete(row)) {
        row._rowVariant = 'success'
      }
      return { row: row, cols: cols }
    },

    // Setor concluído: identificadas + canceladas + em branco = total, 1ª/2ª/3ª contagem em 100% e sem divergentes
    isComplete(row) {
      var q = code => row['c_' + code]
      var pct = code => row['c_' + code + '_pct']
      if (q('TOTAL') == null) return false
      if (q('IDENTIFIED_TOTAL') + q('CANCEL') + q('BLANK_TOTAL') !== q('TOTAL')) return false
      var full = ['COUNT1_TOTAL', 'COUNT2_TOTAL', 'COUNT3'].every(code => pct(code) >= 99.995)
      return full && q('COUNT2') === 0
    },

    async getMonitor(sectorId) {
      var currentUser = this.$store.state.currentUser
      var resp = await axios.get(
        this.$store.state.ws_url + "/app/inventory/monitor?inventoryId=" + this.filters.inventoryId + "&stockLocationId=" + sectorId + "&userId=" + currentUser.id
      )
      return resp.data
    },

    async refreshData() {
      if (!this.filters.inventoryId || this.sectorDDL.length <= 0) {
        return
      }

      var myToken = ++this.refreshToken
      var sectors = [...this.sectorDDL]
      var s = this

      this.isBusy = true
      this.loaded = 0
      this.sectorTotal = sectors.length + 1

      var rowsByKey = {}
      var colsByKey = {}
      var order = []

      // Linha 0 = inventário inteiro (rodapé de totais)
      var all = { value: 0, text: 'Total' }
      var jobs = [all].concat(sectors)

      // Em lotes para não disparar uma chamada por setor de uma só vez
      var batchSize = 5
      for (var i = 0; i < jobs.length; i += batchSize) {
        var batch = jobs.slice(i, i + batchSize)
        await Promise.all(batch.map(async function(sector) {
          try {
            var monitor = await s.getMonitor(sector.value)
            if (myToken !== s.refreshToken) return
            var r = s.toRow(sector, monitor)
            rowsByKey[sector.value] = r.row
            r.cols.forEach(c => { if (!colsByKey[c.key]) { colsByKey[c.key] = c; order.push(c.key) } })
          } catch (error) {
            console.log(error.message)
          }
          s.loaded++
        }))
        if (myToken !== s.refreshToken) return
      }

      this.totals = rowsByKey[0] || null
      this.columns = order.map(k => colsByKey[k])
      this.rows = sectors.filter(sec => rowsByKey[sec.value]).map(sec => rowsByKey[sec.value])
      this.isBusy = false
    },

    async getSector() {
      var s = this
      var currentUser = this.$store.state.currentUser
      await axios.get(
        this.$store.state.ws_url + "/stock/location/label/ddl?inventoryId=" + s.filters.inventoryId + "&uid=" + currentUser.id + "&sid=" + currentUser.sid
      ).then(function(resp) {
        s.sectorDDL = resp.data.obj || []
        s.refreshData()
      }).catch(function(error) {
        console.log(error)
      })
    },

    async getInventory() {
      var s = this
      var currentUser = this.$store.state.currentUser
      await axios.get(
        this.$store.state.ws_url + "/stock/inventory/ddl?companyId=" + s.filters.companyId + "&inventoryId=" + s.filters.inventoryId + "&uid=" + currentUser.id + "&sid=" + currentUser.sid
      ).then(function(resp) {
        s.inventoryDDL = resp.data.obj

        if (s.inventoryDDL && s.inventoryDDL.length > 0) {
          if (!s.filters.inventoryId || s.filters.inventoryId <= 0 || !s.inventoryDDL.some(i => i.value == s.filters.inventoryId)) {
            s.filters.inventoryId = Number(s.inventoryDDL[s.inventoryDDL.length - 1].value)
          }
        }
        s.$utils.setInventoryContext(s.$store.state.config.storageName, s.filters.companyId, s.filters.inventoryId)
        s.getSector()
      }).catch(function(error) {
        console.log(error)
      })
    },

    changeInventory() {
      this.$utils.setInventoryContext(this.$store.state.config.storageName, this.filters.companyId, this.filters.inventoryId)
      this.getSector()
    },

    changeCompany() {
      this.getInventory()
    }
  },
  mounted: function() {
    this.companyDDL = this.$store.state.currentUser.companies

    var stored = this.$utils.getInventoryContext(this.$store.state.config.storageName)
    var validStoredCompany = stored && stored.companyId && this.companyDDL.some(c => c.value == stored.companyId)

    this.filters.companyId = validStoredCompany ? stored.companyId : this.$store.state.currentUser.companyId
    if (validStoredCompany && stored.inventoryId) {
      this.filters.inventoryId = stored.inventoryId
    }

    this.changeCompany()

    const refreshTime = this.$store.state.config.inventoryMonitorRefresh
    if (!this.intervalId) {
      this.intervalId = setInterval(this.refreshData, refreshTime ? refreshTime : 60000)
    }
  },
  beforeDestroy: function() {
    clearInterval(this.intervalId)
  }
}
</script>

<style>
.monitor-sector-table th,
.monitor-sector-table td {
  white-space: nowrap;
  padding: 0.4rem 0.5rem;
  font-size: 1.1em;
  vertical-align: middle;
}
.monitor-sector-table th {
  font-size: 0.85em;
}
/* Tema escuro: linhas finalizadas em verde escuro com texto claro */
.c-dark-theme .monitor-sector-table tbody tr.table-success,
.c-dark-theme .monitor-sector-table tbody tr.table-success > td {
  background-color: #1e5b34 !important;
  color: #e8f5ec !important;
}
.monitor-sector-table th {
  white-space: normal;
  text-transform: uppercase;
}
</style>
